// M146 Fase 3E — Samengevoegd bedrijfsprofiel.
// Vervangt de gestapelde IntakePage + BusinessBrainPage-inbedding in
// /instellingen/bedrijfsprofiel. Toont het profiel én de feiten die
// JustEasySEO uit het gesprek heeft opgeslagen in één rustige weergave.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { EditCompanyBasicsModal } from "@/components/EditCompanyBasicsModal";
import { Pencil, Loader2, MessageCircleQuestion } from "lucide-react";
import { t } from "@/i18n";

const SECTIONS = [
  {
    key: "bedrijf",
    title: "Bedrijf",
    rows: [
      ["Bedrijfsnaam", "company_name"],
      ["Website", "website"],
      ["Branche", "industry"],
      ["Model", "business_model"],
      ["Grootte", "company_size"],
      ["Producten/diensten", "products_services"],
    ],
  },
  {
    key: "doelgroep",
    title: "Doelgroep",
    rows: [
      ["Doelgroep", "target_audience"],
      ["Ideale klant", "ideal_customer"],
      ["Ongewenste klanten", "unwanted_customers"],
      ["Regio's", "regions"],
    ],
  },
  {
    key: "doelen",
    title: "Doelen & capaciteit",
    rows: [
      ["Capaciteit per maand", "capacity_new_customers"],
      ["Seizoenen", "seasons_note"],
      ["Groeidoel", "growth_goals"],
      ["Omzetdoel", "revenue_goals"],
      ["Marketingdoel", "marketing_goals"],
      ["Maandbudget", "monthly_budget_eur"],
      ["Risicoprofiel", "risk_appetite"],
    ],
  },
  {
    key: "merk",
    title: "Merk & schrijfstijl",
    rows: [
      ["Schrijfstijl", "brand_tone"],
      ["Stijl", "brand_style"],
      ["Kernwaarden", "brand_values"],
    ],
  },
];

function mapVal(raw) {
  if (raw == null || raw === "") return null;
  const key = String(raw).toUpperCase();
  const mapped = t(`intake_field_value.${key}`);
  return mapped && !mapped.startsWith("intake_field_value.") ? mapped : raw;
}

function fmtValue(fieldKey, val) {
  if (val == null || val === "") return null;
  if (fieldKey === "regions" && Array.isArray(val)) return val.join(", ");
  if (fieldKey === "monthly_budget_eur") {
    const n = Number(val);
    if (!isFinite(n)) return String(val);
    return `€ ${n.toLocaleString("nl-NL")}`;
  }
  if (["business_model", "risk_appetite"].includes(fieldKey)) return mapVal(val);
  return typeof val === "string" ? val : String(val);
}

const KNOWLEDGE_TYPE_LABEL = {
  FACT: "Feit",
  OBSERVATION: "Waarneming",
  HYPOTHESIS: "Hypothese",
  PREDICTION: "Voorspelling",
  RECOMMENDATION: "Advies",
  LEARNED_PATTERN: "Patroon",
};

function KnowledgeItemRow({ item }) {
  const label = KNOWLEDGE_TYPE_LABEL[item.type] || item.type;
  const inactive = item.status !== "active";
  return (
    <div
      data-testid={`bedrijfsprofiel-fact-${item.id}`}
      className={`rounded-[12px] p-4 ${inactive ? "opacity-60" : ""}`}
      style={{ background: "#F7F8F5" }}
    >
      <div className="flex items-center justify-between gap-3 mb-1.5">
        <span
          className="inline-block text-[10px] font-semibold rounded-full px-2 py-0.5"
          style={{ background: "#E8F5EF", color: "#087F8C", letterSpacing: "0.04em" }}
        >
          {label}
        </span>
        {item.confidence != null && (
          <span className="text-[11px] text-text-muted tabular-nums">
            zekerheid {Math.round(item.confidence * 100)}%
          </span>
        )}
      </div>
      <p className="text-sm text-text-primary leading-relaxed">{item.content}</p>
    </div>
  );
}

export default function BedrijfsprofielView() {
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [knowledge, setKnowledge] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, k] = await Promise.all([
        api.get("/business-brain/profile"),
        api.get("/business-brain/knowledge"),
      ]);
      setProfile(p.data.profile || null);
      setKnowledge((k.data.items || []).filter((x) => x.status === "active"));
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Kon bedrijfsprofiel niet laden.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-text-muted" data-testid="bedrijfsprofiel-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Bedrijfsprofiel laden…
      </div>
    );
  }
  if (err) {
    return <div className="text-sm text-warn-amber" data-testid="bedrijfsprofiel-err">{err}</div>;
  }
  if (!profile) {
    return (
      <div className="rounded-[16px] p-8 bg-bg-panel shadow-card text-center"
            data-testid="bedrijfsprofiel-empty">
        <p className="text-sm text-text-muted mb-4">Nog geen bedrijfsprofiel — start de intake om JustEasySEO te leren over je bedrijf.</p>
        <button onClick={() => nav("/intake")}
                 className="inline-flex items-center gap-2 rounded-[12px] px-5 py-2.5 bg-accent-teal text-white text-sm font-medium hover:bg-accent-teal-dim">
          Start intake <MessageCircleQuestion className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bedrijfsprofiel-view">
      {/* Header met bewerkknop */}
      <div className="rounded-[16px] p-6 bg-accent-mint flex items-center justify-between gap-4 flex-wrap"
            data-testid="bedrijfsprofiel-header">
        <div>
          <div className="text-sm text-text-primary font-medium">
            {profile.company_name || "Bedrijfsprofiel"}
          </div>
          <div className="text-sm text-text-primary/70 mt-0.5">
            Dit gebruikt JustEasySEO bij elk advies en elk bericht.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setEditOpen(true)}
            data-testid="bedrijfsprofiel-edit-btn"
            className="inline-flex items-center gap-1.5 text-sm text-accent-teal-dim hover:text-accent-teal rounded-[10px] px-3 py-1.5 bg-bg-panel border border-border-hairline hover:bg-bg-panel-raised"
          >
            <Pencil className="w-3.5 h-3.5" /> Bedrijfsgegevens wijzigen
          </button>
          <button
            onClick={() => nav("/intake")}
            data-testid="bedrijfsprofiel-open-intake"
            className="inline-flex items-center gap-1.5 text-sm text-accent-teal-dim hover:text-accent-teal"
          >
            Volledige intake openen →
          </button>
        </div>
      </div>

      {/* Profiel-secties */}
      {SECTIONS.map((sec) => (
        <section key={sec.key}
                  className="rounded-[16px] p-6 bg-bg-panel shadow-card"
                  data-testid={`bedrijfsprofiel-section-${sec.key}`}
                  style={{ boxShadow: "0 1px 3px rgba(23,44,50,0.06)" }}>
          <h3 className="text-lg font-bold text-text-primary mb-4">{sec.title}</h3>
          <dl className="grid md:grid-cols-2 gap-x-8 gap-y-3">
            {sec.rows.map(([label, key]) => {
              const val = fmtValue(key, profile[key]);
              return (
                <div key={key} className="flex justify-between gap-4 py-2 border-b border-border-hairline last:border-0"
                      data-testid={`bedrijfsprofiel-row-${key}`}>
                  <dt className="text-sm text-text-muted font-medium shrink-0">{label}</dt>
                  <dd className="text-sm text-text-primary text-right min-w-0 break-words">
                    {val || <span className="text-text-subtle">—</span>}
                  </dd>
                </div>
              );
            })}
          </dl>
        </section>
      ))}

      {/* Concurrenten */}
      {profile.competitors?.length > 0 && (
        <section className="rounded-[16px] p-6 bg-bg-panel shadow-card"
                  data-testid="bedrijfsprofiel-competitors"
                  style={{ boxShadow: "0 1px 3px rgba(23,44,50,0.06)" }}>
          <h3 className="text-lg font-bold text-text-primary mb-4">Concurrenten</h3>
          <div className="flex flex-wrap gap-2">
            {profile.competitors.map((c, i) => (
              <a key={i} href={c.url || "#"} target="_blank" rel="noreferrer"
                 className="text-sm px-3 py-1.5 rounded-full border border-border-hairline text-text-primary hover:border-accent-teal hover:bg-accent-mint transition-colors">
                {c.name}
              </a>
            ))}
          </div>
        </section>
      )}

      {/* Feiten uit gesprek */}
      <section className="rounded-[16px] p-6 bg-bg-panel shadow-card"
                data-testid="bedrijfsprofiel-facts-section"
                style={{ boxShadow: "0 1px 3px rgba(23,44,50,0.06)" }}>
        <div className="flex items-baseline justify-between mb-4">
          <h3 className="text-lg font-bold text-text-primary">Feiten uit gesprek</h3>
          <span className="text-xs text-text-muted tabular-nums">
            {knowledge.length} {knowledge.length === 1 ? "feit" : "feiten"}
          </span>
        </div>
        {knowledge.length === 0 ? (
          <p className="text-sm text-text-muted">Nog geen feiten opgeslagen.</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {knowledge.slice(0, 12).map((k) => (
              <KnowledgeItemRow key={k.id} item={k} />
            ))}
          </div>
        )}
        {knowledge.length > 12 && (
          <div className="mt-4 text-center">
            <button
              onClick={() => nav("/business-brain")}
              className="text-sm text-accent-teal-dim hover:text-accent-teal"
              data-testid="bedrijfsprofiel-show-all-facts"
            >
              Bekijk alle {knowledge.length} feiten →
            </button>
          </div>
        )}
      </section>

      <EditCompanyBasicsModal
        open={editOpen}
        initial={{ company_name: profile.company_name || "", website: profile.website || "" }}
        onClose={() => setEditOpen(false)}
        onSaved={() => load()}
      />
    </div>
  );
}
